// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: d6c95fb9-e2ce-4aec-8a2b-252ac6480168

// ---------------------------------------------------------------------------
// APPLICATION -> DEPLOYMENT (STAGE) -> SERVERS
//
// The data model has had three levels since Deployment was introduced: an
// Application owns Deployments, a Deployment is a named environment (Epic PRD,
// Epic TST, Epic TRN) and carries the servers it runs on. FactSheetDetail
// rendered all of that as FLAT CHIP ROWS, one row per relation type — so an
// Application showed a "Deployments" row and nothing else, and the servers were
// two clicks away inside each deployment with no indication they existed.
//
// The consequence was not cosmetic. The whole reason Deployment exists is that
// "which app does this server belong to" is not a migratable unit of work: Epic
// TST cuts over on a different date, with different risk, than Epic PRD. A view
// that hides the stage hides the unit of work, and someone looking at an
// application concluded it had no servers at all.
//
// This renders the hierarchy in place. It is READ-ONLY on purpose — editing
// still goes through RelationEditor, which owns the both-ends bookkeeping. A
// second editing path over the same edges would be a way for the two directions
// to disagree.
// ---------------------------------------------------------------------------
import React, { useEffect, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Chip, Stack, Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TypeBadge } from './ui';
import store from './store';
import HelpBubble from './HelpBubble';
import DeploymentDiagram from './DeploymentDiagram';

// Environment is a mandatory SELECT on Deployment, but a row imported before
// the field existed can still be blank — show the deployment rather than
// hiding it behind a missing stage, because a deployment with no declared
// environment is exactly the gap a discovery survey needs to surface.
const stageOf = (d) => (d.fields || {}).environmentLabel || (d.fields || {}).environment || '';

export default function DeploymentTree({ factSheet, onOpen }) {
  const [nodes, setNodes] = useState(null);
  const [view, setView] = useState('list');

  useEffect(() => {
    let alive = true;
    const ids = ((factSheet.relations || {}).deployments || []).map((r) => r.id);
    if (!ids.length) { setNodes([]); return undefined; }
    // Resolved by ID, never by name: ids are uuids and globally unique, whereas
    // two clients can and do have a deployment called "PRD". Name-keyed lookup
    // is how a link into another client's fact sheet has happened before.
    Promise.all(ids.map((id) => store.getFactSheet(id)))
      .then((rows) => {
        if (!alive) return;
        setNodes(rows.filter(Boolean).map((d) => ({
          id: d.id,
          name: d.name,
          stage: stageOf(d),
          lifecycle: (d.fields || {}).lifecycleStatus || '',
          servers: (d.resolvedRelations || {}).servers || [],
        })));
      })
      .catch(() => { if (alive) setNodes([]); });
    return () => { alive = false; };
  }, [factSheet]);

  if (nodes === null) return null;                  // still resolving; say nothing
  if (!nodes.length) {
    return (
      <Box sx={{ mt: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Deployments &amp; servers</Typography>
          <HelpBubble topic="deploymentTree" />
        </Stack>
        {/* Stated, not left blank: "no deployments recorded" is a finding a
            consultant acts on, while an empty area reads as a broken screen. */}
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          No deployments recorded for this application, so no servers are attributed to it yet.
        </Typography>
      </Box>
    );
  }

  const totalServers = nodes.reduce((n, d) => n + d.servers.length, 0);

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Deployments &amp; servers</Typography>
        <HelpBubble topic="deploymentTree" />
        <Chip size="small" label={`${nodes.length} deployment${nodes.length === 1 ? '' : 's'}`} />
        <Chip size="small" label={`${totalServers} server${totalServers === 1 ? '' : 's'}`} />
        <Box sx={{ flex: 1 }} />
        {/* The LIST is the default, deliberately. It is the view that stays
            usable at any size and it is what a person filling the record in
            needs. The diagram answers a different question -- what does this
            stack look like -- and is the one people take into a meeting. */}
        <ToggleButtonGroup size="small" exclusive value={view}
          onChange={(e, v) => v && setView(v)}>
          <ToggleButton value="list" sx={{ py: 0.25, px: 1, fontSize: 11 }}>List</ToggleButton>
          <ToggleButton value="diagram" sx={{ py: 0.25, px: 1, fontSize: 11 }}>Diagram</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {view === 'diagram' && (
        <DeploymentDiagram
          appName={factSheet.name}
          appId={factSheet.id}
          nodes={nodes}
          urls={{
            publicUrl: (factSheet.fields || {}).publicUrl,
            privateUrl: (factSheet.fields || {}).privateUrl,
          }}
          onOpen={onOpen}
        />
      )}

      {view === 'list' && nodes.map((d) => (
        <Accordion key={d.id} disableGutters defaultExpanded={nodes.length <= 3}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <TypeBadge type="Deployment" size={16} />
              <Typography
                sx={{ fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={(e) => { e.stopPropagation(); onOpen(d.id); }}
              >
                {d.name}
              </Typography>
              {d.stage && <Chip size="small" color="primary" variant="outlined" label={d.stage} />}
              {d.lifecycle && <Chip size="small" variant="outlined" label={d.lifecycle} />}
              <Chip size="small" label={`${d.servers.length} server${d.servers.length === 1 ? '' : 's'}`} />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {d.servers.length ? (
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {d.servers.map((s) => (
                  <Chip
                    key={s.id} size="small" label={s.name}
                    icon={<Box sx={{ ml: 0.5 }}><TypeBadge type={s.type} size={16} /></Box>}
                    onClick={() => onOpen(s.id)} sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No servers linked to this deployment.
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
